import { logger } from '@magicschool/logger';
import { EventEmitter } from 'eventemitter3';
import type { TTSOptions, TTSProvider } from './types';

export type Payloads = {
  play: { text: string; provider: string; opts?: TTSOptions };
  pause: { text: string | null };
  resume: { text: string | null };
  stop: { text: string | null };
  ended: { text: string | null };
  reset: { text: string | null };
  error: { error: Error; text: string; provider: string; opts?: TTSOptions };
  abort: { text: string | null; provider: string };
  settings: TTSOptions;
  progress: { text: string | null; progress: number; duration: number | null };
  ready: { text: string | null; provider: string };
};

export interface TTSEvents {
  play: (payload: Payloads['play']) => void;
  pause: (payload: Payloads['pause']) => void;
  resume: (payload: Payloads['resume']) => void;
  stop: (payload: Payloads['stop']) => void;
  ended: (payload: Payloads['ended']) => void;
  reset: (payload: Payloads['reset']) => void;
  error: (payload: Payloads['error']) => void;
  abort: (payload: Payloads['abort']) => void;
  settings: (payload: Payloads['settings']) => void;
  progress: (payload: Payloads['progress']) => void;
  ready: (payload: Payloads['ready']) => void;
}

export class TTSService extends EventEmitter<TTSEvents> {
  private current: string | null = null;
  private isReady = false;
  private streamProgress = 0;
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  private active: TTSProvider;

  constructor(
    private backend: TTSProvider,
    private fallback: TTSProvider,
  ) {
    super();
    this.active = backend.isSupported() ? backend : fallback;

    // Set up any event listeners that may be needed for streaming providers
    if (this.active.onProgress) {
      this.active.onProgress((progress: number, duration: number | null) => {
        if (this.current) {
          this.streamProgress = progress;
          this.emit('progress', { text: this.current, progress, duration });
        }
      });
    }

    if (this.active.onReady) {
      this.active.onReady(() => {
        if (this.current && !this.isReady) {
          this.isReady = true;
          this.emit('ready', { text: this.current, provider: this.active.name });
        }
      });
    }
  }

  setVolume = (v: number): void => {
    this.backend.setVolume?.(v);
    this.fallback.setVolume?.(v);
    this.emit('settings', this.getSettings());
  };

  setRate = (r: number): void => {
    this.backend.setRate?.(r);
    this.fallback.setRate?.(r);
    this.emit('settings', this.getSettings());
  };

  setLang = (lang: string): void => {
    this.backend.setLang?.(lang);
    this.fallback.setLang?.(lang);
    this.emit('settings', this.getSettings());
  };

  setVoice = (voice: string): void => {
    this.backend.setVoice?.(voice);
    this.fallback.setVoice?.(voice);
    this.emit('settings', this.getSettings());
  };

  play = async (text: string, opts?: TTSOptions): Promise<void> => {
    // Clean up any existing progress monitoring
    this.cleanupProgressMonitoring();

    // Reset state
    this.isReady = false;
    this.streamProgress = 0;
    this.current = text;

    let prov = this.backend.isSupported() ? this.backend : this.fallback;
    this.active = prov;

    // Set up ended callback
    prov.setOnEnded?.(() => {
      this.emit('ended', { text: this.current });
      this.cleanupProgressMonitoring();
      this.current = null;
    });

    // Emit play event
    this.emit('play', { text, provider: prov.name, opts });

    try {
      this.setupProgressMonitoring();

      await prov.play(text, opts);

      if (!prov.onReady) {
        this.isReady = true;
        this.emit('ready', { text: this.current, provider: prov.name });
      }
    } catch (error) {
      const err = error as Error;

      if (err.name === 'AbortError') {
        this.emit('abort', { text: this.current, provider: prov.name });
        this.cleanupProgressMonitoring();
        this.current = null;
        return;
      }

      logger.error(`[TTSService] Error in provider ${prov.name}:`, err);
      this.emit('error', { error: err, text, provider: prov.name, opts });

      prov = this.fallback;
      this.active = prov;

      prov.setOnEnded?.(() => {
        this.emit('ended', { text: this.current });
        this.cleanupProgressMonitoring();
        this.current = null;
      });

      // Emit play event for fallback
      this.emit('play', { text, provider: prov.name, opts });

      try {
        await prov.play(text, opts);

        // If the provider doesn't have a ready event, assume it's ready now
        if (!prov.onReady) {
          this.isReady = true;
          this.emit('ready', { text: this.current, provider: prov.name });
        }
      } catch (fallbackError) {
        this.emit('error', { error: fallbackError as Error, text, provider: prov.name, opts });
        this.cleanupProgressMonitoring();
        this.current = null;
      }
    }
  };

  pause = (): void => {
    this.active.pause();
    this.emit('pause', { text: this.current });

    // Pause our progress monitoring
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  };

  resume = async (): Promise<void> => {
    await this.active.resume();
    this.emit('resume', { text: this.current });

    // Resume progress monitoring
    this.setupProgressMonitoring();
  };

  stop = (): void => {
    this.active.stop();
    this.emit('stop', { text: this.current });
    this.cleanupProgressMonitoring();
    this.current = null;
  };

  reset = (): void => {
    this.active.reset?.();
    this.emit('reset', { text: this.current });
    this.cleanupProgressMonitoring();
    this.streamProgress = 0;
  };

  abort = (): void => {
    this.active.abortRequest?.();
    this.emit('abort', { text: this.current, provider: this.active.name });
    this.cleanupProgressMonitoring();
    this.current = null;
  };

  clearCache = (): void => {
    this.backend.clearCache?.();
    this.fallback.clearCache?.();
  };

  isPlaying = (t?: string): boolean => this.active.isPlaying?.(t) ?? false;

  getProgress = (): number => this.streamProgress;

  getSettings = (): TTSOptions => this.active.getSettings?.() ?? {};

  private setupProgressMonitoring(): void {
    // Only set up progress monitoring if the provider doesn't have its own progress event
    if (!this.active.onProgress && this.current) {
      // Setup polling for current time if the provider doesn't emit progress events
      this.progressInterval = setInterval(() => {
        if (this.active.getCurrentTime && this.current) {
          const time = this.active.getCurrentTime();
          const duration = this.active.getDuration?.() ?? null;
          this.streamProgress = time;
          this.emit('progress', {
            text: this.current,
            progress: time,
            duration,
          });
        }
      }, 100);
    }
  }

  private cleanupProgressMonitoring(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}
